import { computed } from 'vue';
import { useSessionStore } from '@/stores/session';
import Permissions, { ROLE_PERMISSIONS } from '@/constants/Permissions';
import type { UserPermissions } from '@/types/permissions';

export function usePermissions() {
  const sessionStore = useSessionStore();

  const userPermissions = computed((): UserPermissions => {
    const employee = sessionStore.connectedEmployee;
    const roleSlug = employee?.role?.slug || null;

    return {
      permissions: roleSlug ? ROLE_PERMISSIONS[roleSlug] || [] : [],
      role: roleSlug,
    };
  });

  function hasPermission(permission: string): boolean {
    return userPermissions.value.permissions.includes(permission);
  }

  function hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((permission) => hasPermission(permission));
  }

  const canReadEmployee = computed(() => hasPermission(Permissions.Employee.Read));
  const canCreateEmployee = computed(() => hasPermission(Permissions.Employee.Create));
  const canUpdateEmployee = computed(() => hasPermission(Permissions.Employee.Update));
  const canDeleteEmployee = computed(() => hasPermission(Permissions.Employee.Delete));

  const canReadForeman = computed(() => hasPermission(Permissions.Foreman.Read));
  const canUpdateForeman = computed(() => hasPermission(Permissions.Foreman.Update));
  const canCreateForeman = computed(() => hasPermission(Permissions.Foreman.Create));
  const canDeleteForeman = computed(() => hasPermission(Permissions.Foreman.Delete));

  const canSeePriceColumnsHardwares = computed(() => hasPermission(Permissions.Hardware.SeePriceColumns));

  const canSync = computed(() => hasPermission(Permissions.Sync));

  return {
    canCreateEmployee,
    canCreateForeman,
    canDeleteEmployee,
    canDeleteForeman,
    canReadEmployee,
    canReadForeman,
    canSeePriceColumnsHardwares,
    canSync,
    canUpdateEmployee,
    canUpdateForeman,
    hasAnyPermission,
    hasPermission,
    userPermissions,
  };
}
